<template>
  <div
    class="vth-compat position-relative"
    :class="$attrs.class"
    :style="$attrs.style"
  >
    <input
      ref="input"
      type="text"
      class="form-control"
      :class="[size ? `form-control-${size}` : '', inputClass]"
      :value="localQuery"
      :placeholder="placeholder"
      :disabled="disabled"
      autocomplete="off"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
      @keydown.down.prevent="move(1)"
      @keydown.up.prevent="move(-1)"
      @keydown.enter.prevent="selectActive"
      @keydown.esc="open = false"
    >
    <div
      v-if="open && matches.length"
      class="vth-dropdown"
      :class="{ 'vth-dropdown--up': dropUp }"
    >
      <button
        v-for="(item, i) in matches"
        :key="i"
        type="button"
        class="vth-item"
        data-cy-id="typeahead--item"
        :class="{ active: i === activeIndex, disabled: isDisabled(item) }"
        :disabled="isDisabled(item)"
        @mousedown.prevent="pick(item)"
        @mouseenter="activeIndex = i"
      >
        <slot
          name="suggestion"
          :data="item"
        >{{ serializer(item) }}</slot>
      </button>
      <button
        v-if="remaining > 0"
        type="button"
        class="vth-item text-center text-primary"
        data-cy-id="typeahead--more"
        @mousedown.prevent="shown += pageSize"
      >
        {{ $t('showMore') }} ({{ remaining }})
      </button>
    </div>
  </div>
</template>

<script>
// Igual que max-height de .vth-dropdown
const DROPDOWN_MAX_HEIGHT = 240

export default {
  name: 'VueTypeaheadBootstrap',
  inheritAttrs: false,
  props: {
    modelValue: { type: String, default: undefined },
    value: { type: String, default: undefined },
    data: { type: Array, default: () => [] },
    serializer: { type: Function, default: item => item },
    disabled: { type: Boolean, default: false },
    disabledValues: { type: Array, default: () => [] },
    showAllResults: { type: Boolean, default: false },
    maxMatches: { type: Number, default: 10 },
    highlightClass: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    size: { type: String, default: '' },
    inputClass: { type: String, default: '' },
    // Con valor, muestra los resultados por paginas con "Ver mas" en lugar de cortar en maxMatches
    pageSize: { type: Number, default: 0 },
  },
  emits: ['update:modelValue', 'input', 'hit'],
  data() {
    return {
      open: false,
      focused: false,
      activeIndex: -1,
      localQuery: this.readValue(),
      shown: this.pageSize,
      dropUp: false,
    }
  },
  computed: {
    filtered() {
      const q = this.localQuery.toLowerCase()
      const list = this.data || []
      if (!this.showAllResults && q) {
        return list.filter(item => String(this.serializer(item)).toLowerCase().includes(q))
      }
      return list
    },
    matches() {
      return this.filtered.slice(0, this.pageSize ? this.shown : this.maxMatches)
    },
    remaining() {
      return this.pageSize ? this.filtered.length - this.matches.length : 0
    },
  },
  watch: {
    data() {
      this.shown = this.pageSize
    },
    modelValue() {
      this.syncFromProp()
    },
    value() {
      this.syncFromProp()
    },
  },
  methods: {
    readValue() {
      const v = this.modelValue !== undefined ? this.modelValue : this.value
      return v == null ? '' : String(v)
    },
    syncFromProp() {
      // Solo sincroniza cambios EXTERNOS (cuando el input no esta enfocado): al tipear, el prop vuelve
      // como eco y sincronizarlo revertiria la letra recien escrita.
      if (this.focused) return
      const s = this.readValue()
      if (s !== this.localQuery) this.localQuery = s
    },
    isDisabled(item) {
      return this.disabledValues.includes(this.serializer(item))
    },
    // Si abajo no cabe la lista (en celular, al final de la pantalla) y arriba hay mas espacio, abre hacia arriba
    placeDropdown() {
      const rect = this.$refs.input?.getBoundingClientRect()
      if (!rect) return
      const below = window.innerHeight - rect.bottom
      this.dropUp = below < DROPDOWN_MAX_HEIGHT && rect.top > below
    },
    onFocus() {
      this.focused = true
      this.placeDropdown()
      this.open = true
    },
    onInput(e) {
      this.localQuery = e.target.value
      this.$emit('update:modelValue', this.localQuery)
      this.$emit('input', this.localQuery)
      this.placeDropdown()
      this.open = true
      this.activeIndex = -1
    },
    onBlur() {
      this.focused = false
      setTimeout(() => { this.open = false }, 150)
    },
    move(dir) {
      if (!this.open) {
        this.open = true
        return
      }
      const n = this.matches.length
      if (!n) return
      this.activeIndex = (this.activeIndex + dir + n) % n
    },
    selectActive() {
      if (this.open && this.activeIndex >= 0 && this.matches[this.activeIndex]) {
        this.pick(this.matches[this.activeIndex])
      }
    },
    pick(item) {
      if (this.isDisabled(item)) return
      const text = String(this.serializer(item))
      this.localQuery = text
      this.$emit('update:modelValue', text) // actualiza v-model (Vue 3)
      this.$emit('input', text) // resetea isUserSelected en los templates
      this.$emit('hit', item) // hit al final -> selectCustomer gana (isUserSelected = true)
      this.open = false
      this.activeIndex = -1
    },
  },
}
</script>

<style scoped>
.vth-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1050;
  max-height: 240px;
  overflow-y: auto;
  background-color: var(--bs-body-bg, #283046);
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 0.357rem;
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.1);
  margin-top: 2px;
  padding: 0.25rem 0;
}
.vth-dropdown--up {
  top: auto;
  bottom: 100%;
  margin-top: 0;
  margin-bottom: 2px;
}
.vth-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.5rem 1rem;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
}
.vth-item:hover:not(.disabled),
.vth-item.active:not(.disabled) {
  background-color: rgba(115, 103, 240, 0.12);
}
.vth-item.disabled {
  cursor: default;
  opacity: 0.65;
}
</style>
